import {LabelItem} from '~redux/types/images';

export interface ReduxStateModels {
  currentModel: Model | undefined;
}

// IMPORTANT: the enum values are defined by the API, don't change them unless the API is updated
export enum InspectionType {
  // NOTE: when updating the values here translation keys need to be updated as well
  // The order here influences the order in which the inspection types are displayed in the creation wizard
  classification = 'classification',
  surfaceInspection = 'surface_inspection',
  anomalyDetection = 'anomaly_detection',
  objectDetection = 'object_detection',
  legacyModel = 'legacy_model',
  keypointDetection = 'keypoint_detection',
  ruleBased = 'rule_based',
  measurement = 'rule_based_measurement',
  dataCodes = 'data_codes',
}

export enum InspectionSubType {
  defect = 'defect',
  feature = 'feature',
  none = 'none',
}

export enum Condition {
  OK = 'OK',
  NOK = 'NOK',
  OTHER = 'OTHER',
}

export interface ModelMetrics {
  accuracy: number; // Range: [0, 1]
  datasetId: 'complete' | 'sample_delta' | 'test'; // DatasetType enumeration
  escapeRate: number; // Range: [0, 1]
  f1Score: number; // Range: [0, 1]
  lastPublishedModelTs: number; // Date-time
  overkillRate: number; // Range: [0, 1]
  thresholdCombination: {
    areaSize: number; // Integer value greater than or equal to 0
    confidence: number; // Float value between 0 and 1
  };
  interpretation: Interpretation;
}

export type MeasurementUnit = 'px' | 'mm' | 'deg';

export interface Measurement {
  name: string;
  nominal: number;
  tolerance: number;
  unit: MeasurementUnit;
}

export type LabelConditionCounts = Record<Condition, number>;

/**
 * Per-model image/annotation counts served by `/models/<project>/counts`. Decoupled from the main
 * model list so big-defect-book projects don't pay the count-aggregation cost on every fetch.
 */
export type ModelCounts = {
  modelId: string;
  imageCount: number;
  humanAnnotationCount: number;
  defectAnnotationCount: number;
  labelConditionCounts: LabelConditionCounts;
};

export type Model = {
  id: string;
  name: string;
  createdAt: number;
  createdBy?: {
    email: string;
    id: string;
    name: string;
  } | null;
  inspectionType: InspectionType;
  labels: LabelItem[]; // the error class labels
  defaultMachineLabel?: string | null;
  automlEnabled: boolean;
  automlPipelineStatusData?: AutoMLPipelineStatusResponse | null;
  imageCount: number;
  humanAnnotationCount: number;
  latestPublishedModelMetrics?: ModelMetrics[] | null;
  latestSavedModelMetrics?: ModelMetrics[] | null;
  latestTrainedModelMetrics?: ModelMetrics[] | null;
  latestPublishPipelineTimestamp?: number | null;
  latestSavedFinetuneTimestamp?: number | null;
  latestPipeline?: LatestPipelineForModel | null;
  inspectionSubtype?: InspectionSubType;
  archived?: boolean;
  latestDeployTimestamp?: number | null;
  latestDeployedModelMetrics?: ModelMetrics[] | null;
  latestPublishedVersion?: string;
  labelConditionCounts?: LabelConditionCounts | null;
  defectAnnotationCount?: number | null;
  hasImagesChanged?: boolean;
  hasConfigChanged?: boolean;
  measurements?: Measurement[];
  latestTrainedInferenceSpeed?: InferenceSpeed | null;
  latestPublishedInferenceSpeed?: InferenceSpeed | null;
};

export type ExtendedModel = Model & {
  accuracy: number | undefined;
};

export type ModelSortingKey = 'name' | 'inspectionType' | 'imageCount' | 'createdAt' | 'accuracy';
